import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { createAppTheme } from '@parkdna/ui/theme';
import { useThemeNameStore } from '@parkdna/ui/theme/themeStore';
import { AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';

interface ErrorFallbackProps {
  error?: Error | unknown;
  resetError?: () => void;
}

export default function ErrorFallback({
  error,
  resetError,
}: ErrorFallbackProps) {
  // Mounted above Providers, so there is no ThemeProvider to read from.
  const themeName = useThemeNameStore((state) => state.themeName);
  const theme = createAppTheme(themeName);

  const errorMessage =
    error instanceof Error ? error.message : String(error || 'Unknown error');

  const handleReload = () => {
    resetError?.();
    window.location.reload();
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AlertCircle
                size={48}
                style={{ color: theme.palette.error.main }}
              />
              <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                  Oops, something went wrong!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We&apos;re sorry for the inconvenience. Please try reloading
                  the page.
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Auto-reported notification */}
            <Alert
              severity="info"
              icon={<CheckCircle size={20} />}
              sx={{
                backgroundColor: 'info.lighter',
                borderColor: 'info.light',
                border: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                This error has been automatically logged
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Our monitoring system has captured this error. If you need
                immediate assistance, please contact support.
              </Typography>
            </Alert>

            {/* Error Message */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Error Message:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: 'error.lighter',
                  borderColor: 'error.light',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    m: 0,
                  }}
                >
                  {errorMessage}
                </Typography>
              </Paper>
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: 'center' }}
            >
              <Button
                variant="contained"
                startIcon={<RotateCcw size={18} />}
                onClick={handleReload}
                size="large"
              >
                Reload Page
              </Button>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ pt: 2 }}
            >
              If reloading doesn&apos;t help or this keeps happening, feel free
              to contact support.
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
